import React from 'react';
import { Box, Typography, Switch, FormControlLabel, TextField } from '@mui/material';
import FormulaValidator from './FormulaValidator';
import { FormulaToken } from './FormulaValidator';
import type { FormulaTestValues } from './formulaUtils';

interface FormulaEditorProps {
    formula: string;
    onFormulaChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    testMode: boolean;
    onTestModeChange: (enabled: boolean) => void;
    isValid: boolean;
    tokens: FormulaToken[];
    validationError?: string;
    calculatedResult: number | null;
    testValues: FormulaTestValues;
    isCalculating?: boolean;
    calculationError?: boolean;
}

/**
 * Formula editor panel.
 *
 * @param {FormulaEditorProps} props
 * @returns {JSX.Element}
 */
const FormulaEditor: React.FC<FormulaEditorProps> = (props) => {
    const {
        formula,
        onFormulaChange,
        testMode,
        onTestModeChange,
        isValid,
        tokens,
        validationError,
        calculatedResult,
        testValues,
        isCalculating,
        calculationError,
    } = props;

    return (
        <Box sx={{ flex: 1, bgcolor: 'gray.10', p: 1, borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography component="h4" sx={{ fontWeight: 500 }}>
                    Formula
                </Typography>
                <FormControlLabel
                    control={
                        <Switch
                            disabled={!isValid || !tokens.length}
                            checked={testMode}
                            onChange={(e) => onTestModeChange(e.target.checked)}
                            size="small"
                        />
                    }
                    label={<Typography variant="body2">Test formula</Typography>}
                    sx={{ m: 0 }}
                />
            </Box>
            <TextField value={formula} onChange={onFormulaChange} placeholder="Your formula" variant="outlined" size="small" fullWidth />
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem', mt: 0.25 }}>
                Type question references (e.g., Q1, Q2, Q3) and operators (+, -, *, /, parentheses) without spaces, or alternatively click
                questions on the left to add them. Example: (Q1+Q2)*Q3
            </Typography>
            {formula.trim() && (
                <FormulaValidator
                    isValid={isValid}
                    tokens={tokens}
                    validationError={validationError}
                    calculatedResult={calculatedResult}
                    testMode={testMode}
                    testValues={testValues}
                    isCalculating={isCalculating}
                    calculationError={calculationError}
                />
            )}
        </Box>
    );
};

export default FormulaEditor;
